import { useAlerts } from "components/Alerts/AlertProvider";
import { SKILL_ENTRYPOINT } from "encamp-shared/src/s3/skills/paths";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { buildSkillFileTree, collectFolderPaths } from "./buildSkillFileTree";
import { mergedDraftFiles } from "./draftSkillFiles";
import {
  initialSkillFileEditorState,
  isSkillFileDraftDirty,
  skillFileEditorReducer,
  type SkillFileEditorAction,
  type SkillFileEditorState,
} from "./skillFileEditorState";
import {
  type SkillFileStore,
  useLocalSkillFileStore,
  useServerSkillFileStore,
} from "./skillFileStores";
import { type UnsavedChangesDialogProps, useUnsavedChangesPrompt } from "./useUnsavedChangesPrompt";

/**
 * What the file tree view drives. Opening another file or starting a new one
 * asks first when the open buffer has unsaved text; those, and create and
 * rename, resolve to whether they went ahead.
 */
export type SkillFileTreeController = {
  loading: boolean;
  loadingContent: boolean;
  writing: boolean;
  deleting: boolean;
  /** Every file in the skill tree. */
  paths: string[];
  selectedPath: string | null;
  draft: string;
  setDraft: (value: string) => void;
  /** The open buffer differs from what's stored. */
  dirty: boolean;
  /** Anything that would be lost by leaving: a dirty buffer, or a draft skill's files. */
  hasUnsavedWork: boolean;
  /** A draft skill's files with the open buffer, for create; null for a saved skill. */
  draftFiles: Record<string, string> | null;
  expandedFolders: ReadonlySet<string>;
  openFile: (path: string) => Promise<boolean>;
  /** Close the open file and blank the editor, for a file not named yet. */
  clearSelection: () => Promise<boolean>;
  toggleFolder: (path: string) => void;
  expandFolder: (folder: string) => void;
  createFile: (path: string, content: string) => Promise<boolean>;
  /** Move a file, or a folder and everything in it. */
  renamePath: (from: string, to: string) => Promise<boolean>;
  handleSave: () => Promise<boolean>;
  handleDelete: () => Promise<void>;
  unsavedChangesDialog: UnsavedChangesDialogProps;
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function draftHasContent(files: Record<string, string> | null): boolean {
  if (!files) {
    return false;
  }
  const paths = Object.keys(files);
  return paths.length > 1 || paths.some((path) => files[path]?.trim());
}

type Dispatch = (action: SkillFileEditorAction) => void;

/** Loads `path` into the buffer; an answer for an older open is dropped by request id. */
function useOpenSkillFile(
  dispatch: Dispatch,
  storeRef: { current: SkillFileStore },
  nextRequestId: () => number
) {
  const alerts = useAlerts();
  return useCallback(
    async (path: string): Promise<boolean> => {
      const requestId = nextRequestId();
      dispatch({ type: "openStarted", path, requestId });
      try {
        const result = storeRef.current.read(path);
        const content = typeof result === "string" ? result : await result;
        dispatch({ type: "openLoaded", requestId, content });
        return true;
      } catch (error) {
        dispatch({ type: "openFailed", requestId });
        alerts.error(errorMessage(error, "Failed to load file"));
        return false;
      }
    },
    [alerts, dispatch, nextRequestId, storeRef]
  );
}

/** Edits that write to the store. */
function useSkillFileWrites(params: {
  dispatch: Dispatch;
  stateRef: { current: SkillFileEditorState };
  storeRef: { current: SkillFileStore };
  nextRequestId: () => number;
  readOnly: boolean;
}) {
  const { dispatch, stateRef, storeRef, nextRequestId, readOnly } = params;
  const alerts = useAlerts();

  /** Run `action` with `pending` set, alerting on failure. True when it succeeded. */
  const run = useCallback(
    async (
      pending: SkillFileEditorState["pending"],
      fallback: string,
      action: () => Promise<void>
    ): Promise<boolean> => {
      dispatch({ type: "pending", pending });
      try {
        await action();
        return true;
      } catch (error) {
        dispatch({ type: "pending", pending: null });
        alerts.error(errorMessage(error, fallback));
        return false;
      }
    },
    [alerts, dispatch]
  );

  const save = useCallback(async () => {
    const { selectedPath, draft } = stateRef.current;
    if (!selectedPath || readOnly) {
      return false;
    }
    return run("write", "Failed to save file", async () => {
      await storeRef.current.write(selectedPath, draft, { create: false });
      dispatch({ type: "saved", path: selectedPath, content: draft });
      alerts.success(storeRef.current.savedMessage);
    });
  }, [alerts, dispatch, readOnly, run, stateRef, storeRef]);

  const createFile = useCallback(
    (path: string, content: string) =>
      run("write", "Failed to create file", async () => {
        await storeRef.current.write(path, content, { create: true });
        dispatch({ type: "fileCreated", path, content, requestId: nextRequestId() });
      }),
    [dispatch, nextRequestId, run, storeRef]
  );

  const renamePath = useCallback(
    (from: string, to: string) =>
      run("move", "Failed to rename", async () => {
        await storeRef.current.move(from, to);
        dispatch({ type: "moved", from, to });
      }),
    [dispatch, run, storeRef]
  );

  const handleDelete = useCallback(async () => {
    const { selectedPath } = stateRef.current;
    if (!selectedPath || readOnly || selectedPath === SKILL_ENTRYPOINT) {
      return;
    }
    await run("delete", "Failed to delete file", async () => {
      await storeRef.current.remove(selectedPath);
      dispatch({ type: "deleted", path: selectedPath });
      alerts.success("File deleted");
    });
  }, [alerts, dispatch, readOnly, run, stateRef, storeRef]);

  return { save, createFile, renamePath, handleDelete };
}

/** Opening another file or starting a new one asks first when the buffer is dirty. */
function useGuardedNavigation(params: {
  dispatch: Dispatch;
  stateRef: { current: SkillFileEditorState };
  readOnly: boolean;
  ask: ReturnType<typeof useUnsavedChangesPrompt>["ask"];
  save: () => Promise<boolean>;
  load: (path: string) => Promise<boolean>;
  nextRequestId: () => number;
}) {
  const { dispatch, stateRef, readOnly, ask, save, load, nextRequestId } = params;

  /** True once the open buffer is saved or the user chose to drop it. */
  const confirmLeave = useCallback(async () => {
    const current = stateRef.current;
    if (!isSkillFileDraftDirty(current)) {
      return true;
    }
    const choice = await ask({
      canSave: Boolean(current.selectedPath) && !readOnly,
      message: current.selectedPath
        ? `${current.selectedPath} has unsaved changes.`
        : "The new file isn't named yet, so its text can't be saved.",
    });
    if (choice === "save") {
      return save();
    }
    return choice === "discard";
  }, [ask, readOnly, save, stateRef]);

  const openFile = useCallback(
    async (path: string) => {
      if (path === stateRef.current.selectedPath) {
        return true;
      }
      return (await confirmLeave()) && load(path);
    },
    [confirmLeave, load, stateRef]
  );

  const clearSelection = useCallback(async () => {
    if (!(await confirmLeave())) {
      return false;
    }
    dispatch({ type: "cleared", requestId: nextRequestId() });
    return true;
  }, [confirmLeave, dispatch, nextRequestId]);

  return { openFile, clearSelection };
}

/** Once per tree: expand every folder and open SKILL.md (or the first file). */
function useInitialFileOpen(params: {
  active: boolean;
  initialized: boolean;
  store: SkillFileStore;
  dispatch: Dispatch;
  load: (path: string) => Promise<boolean>;
}) {
  const { active, initialized, store, dispatch, load } = params;
  const { loading, paths } = store;
  useEffect(() => {
    if (!active || initialized || loading || paths.length === 0) {
      return;
    }
    dispatch({ type: "initialized", folders: collectFolderPaths(buildSkillFileTree(paths)) });
    const first = paths.includes(SKILL_ENTRYPOINT) ? SKILL_ENTRYPOINT : paths[0];
    if (first) {
      void load(first);
    }
  }, [active, dispatch, initialized, load, loading, paths]);
}

/**
 * The Manage Skills file tree: a draft skill's files held locally until it's
 * created, or a saved skill's files on the server. `skillId` null with
 * `active` means a draft; a new skill id or draft starts the editor over.
 */
export function useSkillFileEditor(params: {
  active: boolean;
  skillId: string | null;
  tenantId: string | undefined;
  readOnly: boolean;
}): SkillFileTreeController {
  const { active, skillId, tenantId, readOnly } = params;
  const sessionKey = active ? (skillId ?? "draft") : "inactive";

  const localStore = useLocalSkillFileStore(sessionKey);
  const serverStore = useServerSkillFileStore({ skillId: active ? skillId : null, tenantId });
  const store = skillId ? serverStore : localStore;
  const storeRef = useRef(store);
  storeRef.current = store;

  const [state, dispatch] = useReducer(
    skillFileEditorReducer,
    sessionKey,
    initialSkillFileEditorState
  );
  if (state.sessionKey !== sessionKey) {
    dispatch({ type: "reset", sessionKey });
  }
  const stateRef = useRef(state);
  stateRef.current = state;

  // Never reset, so no answer from an earlier tree or open can match a later one.
  const requestCounter = useRef(0);
  const nextRequestId = useCallback(() => {
    requestCounter.current += 1;
    return requestCounter.current;
  }, []);

  const { ask, dialogProps } = useUnsavedChangesPrompt();
  const load = useOpenSkillFile(dispatch, storeRef, nextRequestId);
  const writes = useSkillFileWrites({ dispatch, stateRef, storeRef, nextRequestId, readOnly });
  const { save } = writes;

  const { openFile, clearSelection } = useGuardedNavigation({
    dispatch,
    stateRef,
    readOnly,
    ask,
    save,
    load,
    nextRequestId,
  });

  useInitialFileOpen({ active, initialized: state.initialized, store, dispatch, load });

  const setDraft = useCallback((draft: string) => dispatch({ type: "edited", draft }), []);
  const toggleFolder = useCallback((path: string) => dispatch({ type: "folderToggled", path }), []);
  const expandFolder = useCallback(
    (folder: string) => dispatch({ type: "folderExpanded", folder }),
    []
  );

  const dirty = isSkillFileDraftDirty(state);
  const draftFiles = useMemo(
    () =>
      store.localFiles ? mergedDraftFiles(store.localFiles, state.selectedPath, state.draft) : null,
    [state.draft, state.selectedPath, store.localFiles]
  );

  return {
    loading: store.loading,
    loadingContent: state.loadingContent,
    writing: state.pending === "write" || state.pending === "move",
    deleting: state.pending === "delete",
    paths: store.paths,
    selectedPath: state.selectedPath,
    draft: state.draft,
    setDraft,
    dirty,
    hasUnsavedWork: dirty || draftHasContent(store.localFiles),
    draftFiles,
    expandedFolders: state.expandedFolders,
    openFile,
    clearSelection,
    toggleFolder,
    expandFolder,
    createFile: writes.createFile,
    renamePath: writes.renamePath,
    handleSave: save,
    handleDelete: writes.handleDelete,
    unsavedChangesDialog: dialogProps,
  };
}
